import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { RTVIClient, RTVIEvent } from '@pipecat-ai/client-js';
import { WebSocketTransport } from '@pipecat-ai/websocket-transport';
import { Mic, MicOff, Volume2, VolumeX, X, Clock, Play, Presentation, Code, FileText, Eraser, BookOpen, Sparkles, PenTool, MessageSquare, ChevronRight, CheckCircle2, Trophy, Zap, Brain, RotateCcw, Target, Star, TrendingUp, HelpCircle, Timer } from 'lucide-react';
import DiagramRenderer from './DiagramRenderer';
import SessionManager from '../utils/sessionManager';

// Build headers with JWT Authorization for authenticated API calls
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

// ═══════════════════════════════════════
// INTERNAL MESSAGE FILTERING
// ═══════════════════════════════════════
const INTERNAL_PHRASES = [
  "i've greeted", "i've listed", "i am beginning", "commencing",
  "i've initiated", "i've placed", "i'm focusing", "the initial language",
  "initiating instruction", "commencing core", "initiating topic",
  "beginning the introduction", "starting with module", "proceeding to",
  "i will now", "let me begin by", "moving on to the next",
  "i'll start by", "i have greeted", "i've started", "now i will",
  "i am now", "i'm starting", "i'm going to", "i shall",
  "writing on whiteboard", "let me write", "let me draw",
  "let me show you", "here is the", "here's the",
  "i've written", "i've drawn", "i've shown",
  "(the candidate", "(the student", "(still silent",
  "(continue the", "(move on to", "(ask the next",
  "session started for", "begin the interview",
  "time_up:", "resumed session",
  // Bot reasoning / thinking leaks
  "acknowledge language preference", "i've registered", "i've noted",
  "i'm now adopting", "mixed-language approach", "acknowledging their",
  "i'm ready to seamlessly", "language switch", "[language_switch]",
  "i will adapt the difficulty", "detected that", "registered the",
  "switching to", "adapting my approach", "noted the user",
  "the user's openness", "i'm acknowledging", "i've acknowledged",
  "commencing teaching", "beginning instruction", "starting instruction",
  "initiating the session", "starting the session", "beginning the session",
  "moving to the next topic", "transitioning to", "let's transition",
  "internal note", "system note", "note to self",
  "concluding the session", "i've completed all required",
  "my focus now is on", "i am ready to trigger",
  "trigger the concluding", "closing this teaching session",
  "end_teaching", "[end_teaching]", "[end_session]",
  "<noise>", "noise detected"
];

const isInternalMessage = (text) => {
  if (!text || text.length < 10) return true;
  const lower = text.toLowerCase().trim();
  if (lower.replace(/[^a-z]/g, '').length < 5) return true;
  return INTERNAL_PHRASES.some(phrase => lower.includes(phrase));
};

// Remove all special markers from display text
const cleanDisplayText = (text) => {
  return text
    .replace(/\[WHITEBOARD\][\s\S]*?\[\/WHITEBOARD\]/gi, '')
    .replace(/\[CODE\][\s\S]*?\[\/CODE\]/gi, '')
    .replace(/\[DIAGRAM\][\s\S]*?\[\/DIAGRAM\]/gi, '')
    .replace(/\[QUIZ\][\s\S]*?\[\/QUIZ\]/gi, '')
    .replace(/\[TOPIC_DONE\][\s\S]*?\[\/TOPIC_DONE\]/gi, '')
    .replace(/\[CHALLENGE\][\s\S]*?\[\/CHALLENGE\]/gi, '')
    .replace(/\[EXPLAIN\][\s\S]*?\[\/EXPLAIN\]/gi, '')
    .replace(/\[DIFFICULTY\][\s\S]*?\[\/DIFFICULTY\]/gi, '')
    .replace(/\[FLASHCARD\][\s\S]*?\[\/FLASHCARD\]/gi, '')
    // Unclosed markers
    .replace(/\[WHITEBOARD\][\s\S]*$/gi, '').replace(/\[CODE\][\s\S]*$/gi, '')
    .replace(/\[DIAGRAM\][\s\S]*$/gi, '').replace(/\[QUIZ\][\s\S]*$/gi, '')
    .replace(/\[TOPIC_DONE\][\s\S]*$/gi, '').replace(/\[CHALLENGE\][\s\S]*$/gi, '')
    .replace(/\[EXPLAIN\][\s\S]*$/gi, '').replace(/\[DIFFICULTY\][\s\S]*$/gi, '')
    .replace(/\[FLASHCARD\][\s\S]*$/gi, '')
    // Slide markers
    .replace(/\[SLIDE:\d+\]/gi, '')
    .replace(/\[NEXT_SLIDE\]/gi, '')
    // Language switch and other inline markers
    .replace(/\[LANGUAGE_SWITCH\]/gi, '')
    .replace(/\[SWITCH_LANGUAGE\]/gi, '')
    .replace(/\[AUTO_END\]/gi, '')
    .replace(/\[END_SESSION\]/gi, '')
    .replace(/\[END_TEACHING\]/gi, '')
    .replace(/\[END_INTERVIEW\]/gi, '')
    .replace(/\[INTERNAL\][\s\S]*?\[\/INTERNAL\]/gi, '')
    // Stray tags
    .replace(/\[\/?(?:WHITEBOARD|CODE|DIAGRAM|QUIZ|TOPIC_DONE|CHALLENGE|EXPLAIN|DIFFICULTY|FLASHCARD|SLIDE|NEXT_SLIDE|LANGUAGE_SWITCH|INTERNAL|AUTO_END|END_SESSION|END_TEACHING|END_INTERVIEW)\]/gi, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*+/g, '')
    .replace(/Writing on whiteboard:/gi, '').replace(/Let me draw a diagram:/gi, '')
    .replace(/Writing code:/gi, '').replace(/Let me show you/gi, '')
    .replace(/Here is the code:/gi, '').replace(/Here's the code:/gi, '')
    // Remove bot reasoning prefixes that leak
    .replace(/^(Acknowledge|Acknowledging|Registered|Noted|Detected|Switching|Adapting|Internal|Concluding|Commencing|Initiating|Beginning)[\s\S]*?\.\s*/i, '')
    .trim();
};

// ═══════════════════════════════════════
// QUIZ PARSER
// ═══════════════════════════════════════
const parseQuiz = (quizText) => {
  // Format: question|A) opt1|B) opt2|C) opt3|D) opt4|correct:B
  const parts = quizText.split('|').map(s => s.trim());
  if (parts.length < 3) return null;
  const question = parts[0];
  const correctPart = parts.find(p => p.toLowerCase().startsWith('correct:'));
  const correctAnswer = correctPart ? correctPart.split(':')[1].trim().toUpperCase() : '';
  const options = parts.filter(p => /^[A-D]\)/.test(p));
  return { question, options, correctAnswer };
};

// ═══════════════════════════════════════
// FLASHCARD PARSER
// ═══════════════════════════════════════
const parseFlashcard = (text) => {
  const parts = text.split('|').map(s => s.trim());
  return { term: parts[0] || '', definition: parts[1] || '' };
};

// ═══════════════════════════════════════
// CHALLENGE PARSER
// ═══════════════════════════════════════
const parseChallenge = (text) => {
  const parts = text.split('|').map(s => s.trim());
  const description = parts[0] || '';
  const timePart = parts.find(p => p.toLowerCase().startsWith('time:'));
  const time = timePart ? parseInt(timePart.split(':')[1]) || 60 : 60;
  return { description, time };
};


const LiveCourseTeaching = forwardRef(({
  courseId, moduleId, courseTitle, moduleTitle,
  candidateName = "Student", candidateEmail = "", candidateId = "",
  duration = 10, initialTimeRemaining = null, resumeSummary = "",
  onTeachingEnd = () => {}, onExit = null,
  moduleTopics = [] // Pass topics list from parent for progress tracking
}, ref) => {
  // ═══ Core state ═══
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMicEnabled, setIsMicEnabled] = useState(false);
  const [isSpeakerEnabled, setIsSpeakerEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Ready');
  const [timeRemaining, setTimeRemaining] = useState(initialTimeRemaining || duration * 60);
  const [transcript, setTranscript] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('male-1');
  const [currentTeacherText, setCurrentTeacherText] = useState('');
  const [activeView, setActiveView] = useState('whiteboard');
  const [whiteboardLines, setWhiteboardLines] = useState([]);
  const [codeContent, setCodeContent] = useState('');
  const [notesContent, setNotesContent] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [lastMessageIndex, setLastMessageIndex] = useState(0);
  const [textQueue, setTextQueue] = useState([]);
  const [diagrams, setDiagrams] = useState([]);
  const [showTranscript, setShowTranscript] = useState(true);

  // ═══ Interactive features state ═══
  const [completedTopics, setCompletedTopics] = useState([]);
  const [currentQuiz, setCurrentQuiz] = useState(null); // { question, options, correctAnswer }
  const [quizAnswer, setQuizAnswer] = useState(null);
  const [quizResult, setQuizResult] = useState(null); // 'correct' | 'wrong'
  const [quizStats, setQuizStats] = useState({ correct: 0, total: 0 });
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [challengeTimer, setChallengeTimer] = useState(0);
  const [challengeActive, setChallengeActive] = useState(false);
  const [explainTopic, setExplainTopic] = useState(null);
  const [difficultyLevel, setDifficultyLevel] = useState('beginner');
  const [flashcards, setFlashcards] = useState([]);
  const [currentFlashcardIndex, setCurrentFlashcardIndex] = useState(0);
  const [flashcardFlipped, setFlashcardFlipped] = useState(false);
  const [showFlashcardReview, setShowFlashcardReview] = useState(false);
  const [engagementScore, setEngagementScore] = useState(75);
  const [interactionCount, setInteractionCount] = useState(0);
  const [showEndSessionPopup, setShowEndSessionPopup] = useState(false);
  const endSessionDetectedRef = useRef(false);

  // ═══ Slide deck state (from PPT upload) ═══
  const [moduleSlides, setModuleSlides] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [slidesLoaded, setSlidesLoaded] = useState(false);

  useImperativeHandle(ref, () => ({
    getSessionState: () => ({ timeRemaining, transcript, sessionId, whiteboardLines, codeContent })
  }), [timeRemaining, transcript, sessionId, whiteboardLines, codeContent]);

  const voiceOptions = [
    { value: 'female-1', label: 'Priya - Friendly & Expressive', gender: 'female', emoji: '👩‍🏫' },
    { value: 'female-2', label: 'Ananya - Firm & Authoritative', gender: 'female', emoji: '👩‍💼' },
    { value: 'female-indian-1', label: 'Deepika - Warm & Engaging', gender: 'female', emoji: '👩‍🏫' },
    { value: 'male-1', label: 'Arjun - Upbeat & Energetic', gender: 'male', emoji: '👨‍🏫' },
    { value: 'male-2', label: 'Vikram - Calm & Professional', gender: 'male', emoji: '👨‍💼' },
    { value: 'male-indian-1', label: 'Rahul - Enthusiastic & Clear', gender: 'male', emoji: '👨‍🏫' },
  ];

  // ═══ Fetch pre-stored slides for this module (from PPT upload) ═══
  useEffect(() => {
    if (!courseId || !moduleId) return;
    const fetchSlides = async () => {
      try {
        const API = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';
        const res = await fetch(`${API}/slides/course/${courseId}/module/${moduleId}`, {
          headers: buildAuthHeaders(),
        });
        if (res.ok) {
          const slides = await res.json();
          if (slides && slides.length > 0) {
            setModuleSlides(slides);
            setSlidesLoaded(true);
            setActiveView('slides');
            console.log(`Loaded ${slides.length} pre-stored slides for module`);
          }
        }
      } catch (e) {
        console.error('Failed to fetch module slides:', e);
      }
    };
    fetchSlides();
  }, [courseId, moduleId]);

  // ═══ Refs ═══
  const rtviClientRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioElementRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const whiteboardRef = useRef(null);
  const transcriptRef = useRef(null);
  const textTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const textQueueRef = useRef(null);
  const isBotSpeakingRef = useRef(false);
  const botSpeakingCooldownRef = useRef(null);
  const challengeIntervalRef = useRef(null);

  const addLog = useCallback((msg) => console.log(`[Teaching] ${msg}`), []);

  useEffect(() => { setTimeRemaining(initialTimeRemaining || duration * 60); }, [duration, initialTimeRemaining]);

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptRef.current) transcriptRef.current.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: 'smooth' });
  }, [transcript]);

  // Auto-scroll whiteboard
  useEffect(() => {
    if (whiteboardRef.current) whiteboardRef.current.scrollTo({ top: whiteboardRef.current.scrollHeight, behavior: 'smooth' });
  }, [whiteboardLines, diagrams]);

  // Update engagement score based on interactions
  useEffect(() => {
    if (interactionCount > 0) {
      const base = 60;
      const bonus = Math.min(interactionCount * 5, 40);
      setEngagementScore(Math.min(base + bonus, 100));
    }
  }, [interactionCount]);

  // Challenge timer countdown
  useEffect(() => {
    if (challengeActive && challengeTimer > 0) {
      challengeIntervalRef.current = setInterval(() => {
        setChallengeTimer(prev => {
          if (prev <= 1) {
            setChallengeActive(false);
            clearInterval(challengeIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (challengeIntervalRef.current) clearInterval(challengeIntervalRef.current); };
  }, [challengeActive]);

  const getApiBaseUrl = useCallback(() => {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return `${protocol}//${hostname}:8000`;
    const backendUrl = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';
    return backendUrl.replace('/api', '');
  }, []);

  const setupAudioTrack = useCallback((track) => {
    if (audioElementRef.current) {
      const stream = new MediaStream([track]);
      audioElementRef.current.srcObject = stream;
      audioElementRef.current.play().catch(() => {});
    }
  }, []);

  const showTeacherText = useCallback((text) => {
    if (!text?.trim()) return;
    const cleaned = cleanDisplayText(text);
    if (!cleaned || cleaned.length < 5 || isInternalMessage(cleaned)) return;
    setCurrentTeacherText(cleaned);
    if (textTimeoutRef.current) clearTimeout(textTimeoutRef.current);
    const wordCount = text.split(' ').length;
    const displayTime = Math.max(4000, Math.min(wordCount * 250, 15000));
    textTimeoutRef.current = setTimeout(() => setCurrentTeacherText(''), displayTime);
  }, []);

  // ═══════════════════════════════════════
  // PROCESS ALL MARKER TYPES
  // ═══════════════════════════════════════
  const processMarkers = useCallback((text) => {
    let hasMarkers = false;

    // Whiteboard
    const wbRegex = /\[WHITEBOARD\]([\s\S]*?)\[\/WHITEBOARD\]/gi;
    let match;
    while ((match = wbRegex.exec(text)) !== null) {
      const content = match[1].trim();
      if (content) {
        hasMarkers = true;
        const lines = content.split('\n').filter(l => l.trim());
        setActiveView('whiteboard');
        lines.forEach((line, i) => setTimeout(() => setWhiteboardLines(prev => [...prev, line]), i * 300));
      }
    }

    // Code
    const codeRegex = /\[CODE\]([\s\S]*?)\[\/CODE\]/gi;
    while ((match = codeRegex.exec(text)) !== null) {
      const code = match[1].trim();
      if (code) { hasMarkers = true; setCodeContent(prev => prev + (prev ? '\n\n' : '') + code); setActiveView('code'); }
    }

    // Diagram
    const diagRegex = /\[DIAGRAM\]([\s\S]*?)\[\/DIAGRAM\]/gi;
    while ((match = diagRegex.exec(text)) !== null) {
      const desc = match[1].trim();
      if (desc) { hasMarkers = true; setDiagrams(prev => [...prev, { description: desc, timestamp: Date.now() }]); setActiveView('whiteboard'); }
    }

    // Topic Done
    const topicRegex = /\[TOPIC_DONE\]([\s\S]*?)\[\/TOPIC_DONE\]/gi;
    while ((match = topicRegex.exec(text)) !== null) {
      const topic = match[1].trim();
      if (topic) { hasMarkers = true; setCompletedTopics(prev => prev.includes(topic) ? prev : [...prev, topic]); }
    }

    // Quiz
    const quizRegex = /\[QUIZ\]([\s\S]*?)\[\/QUIZ\]/gi;
    while ((match = quizRegex.exec(text)) !== null) {
      const quizData = parseQuiz(match[1].trim());
      if (quizData) { hasMarkers = true; setCurrentQuiz(quizData); setQuizAnswer(null); setQuizResult(null); setInteractionCount(prev => prev + 1); }
    }

    // Challenge
    const challengeRegex = /\[CHALLENGE\]([\s\S]*?)\[\/CHALLENGE\]/gi;
    while ((match = challengeRegex.exec(text)) !== null) {
      const challengeData = parseChallenge(match[1].trim());
      if (challengeData) { hasMarkers = true; setCurrentChallenge(challengeData); setChallengeTimer(challengeData.time); setChallengeActive(true); setInteractionCount(prev => prev + 1); }
    }

    // Explain
    const explainRegex = /\[EXPLAIN\]([\s\S]*?)\[\/EXPLAIN\]/gi;
    while ((match = explainRegex.exec(text)) !== null) {
      const topic = match[1].trim();
      if (topic) { hasMarkers = true; setExplainTopic(topic); setInteractionCount(prev => prev + 1); }
    }

    // Difficulty
    const diffRegex = /\[DIFFICULTY\]([\s\S]*?)\[\/DIFFICULTY\]/gi;
    while ((match = diffRegex.exec(text)) !== null) {
      const level = match[1].trim().toLowerCase();
      if (['beginner', 'intermediate', 'advanced'].includes(level)) { hasMarkers = true; setDifficultyLevel(level); }
    }

    // Flashcard
    const flashRegex = /\[FLASHCARD\]([\s\S]*?)\[\/FLASHCARD\]/gi;
    while ((match = flashRegex.exec(text)) !== null) {
      const fc = parseFlashcard(match[1].trim());
      if (fc.term) {
        hasMarkers = true;
        setFlashcards(prev => [...prev, fc]);
        if (!showFlashcardReview) setShowFlashcardReview(true);
      }
    }

    // Slide navigation markers (from PPT-based teaching)
    const slideNumRegex = /\[SLIDE:(\d+)\]/gi;
    while ((match = slideNumRegex.exec(text)) !== null) {
      const slideNum = parseInt(match[1]);
      if (slideNum >= 1 && slideNum <= moduleSlides.length) {
        hasMarkers = true;
        setCurrentSlideIndex(slideNum - 1);
        if (slidesLoaded) setActiveView('slides');
      }
    }
    if (/\[NEXT_SLIDE\]/gi.test(text)) {
      hasMarkers = true;
      setCurrentSlideIndex(prev => Math.min(prev + 1, moduleSlides.length - 1));
      if (slidesLoaded) setActiveView('slides');
    }

    // Unclosed markers fallback
    if (!hasMarkers) {
      const unclosedWB = /\[WHITEBOARD\]([\s\S]+)$/gi.exec(text);
      if (unclosedWB) { const lines = unclosedWB[1].trim().split('\n').filter(l => l.trim()); setActiveView('whiteboard'); lines.forEach((l, i) => setTimeout(() => setWhiteboardLines(prev => [...prev, l]), i * 300)); hasMarkers = true; }
      const unclosedCode = /\[CODE\]([\s\S]+)$/gi.exec(text);
      if (unclosedCode) { setCodeContent(prev => prev + (prev ? '\n\n' : '') + unclosedCode[1].trim()); setActiveView('code'); hasMarkers = true; }
    }

    return hasMarkers;
  }, [showFlashcardReview]);

  const queueTeacherText = useCallback((text, isWhiteboard = false, isCode = false, isDiagram = false) => {
    if (isWhiteboard) { const lines = text.split('\n').filter(l => l.trim()); setActiveView('whiteboard'); lines.forEach((l, i) => setTimeout(() => setWhiteboardLines(prev => [...prev, l]), i * 300)); }
    else if (isCode) { setCodeContent(prev => prev + (prev ? '\n\n' : '') + text); setActiveView('code'); }
    else if (isDiagram) { setDiagrams(prev => [...prev, { description: text, timestamp: Date.now() }]); setActiveView('whiteboard'); }
    else if (text) { showTeacherText(text); setTranscript(prev => [...prev, { speaker: 'teacher', text, timestamp: new Date().toISOString() }]); }
  }, [showTeacherText]);

  // ═══════════════════════════════════════
  // POLL TRANSCRIPTS
  // ═══════════════════════════════════════
  const pollTranscripts = useCallback(async () => {
    if (!sessionId) return;
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/course-teaching/live-transcript/${sessionId}`, {
        headers: buildAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        const messages = data.messages || [];
        if (messages.length > lastMessageIndex) {
          const newMessages = messages.slice(lastMessageIndex);
          setLastMessageIndex(messages.length);
          newMessages.forEach(msg => {
            if (msg.speaker === 'teacher' && msg.text) {
              // Detect end-of-teaching signal from bot
              if ((msg.text.includes('[END_TEACHING]') || msg.text.includes('[END_SESSION]')) && !endSessionDetectedRef.current) {
                endSessionDetectedRef.current = true;
                setShowEndSessionPopup(true);
              }
              // Process all markers
              processMarkers(msg.text);
              // Clean display text
              const displayText = cleanDisplayText(msg.text);
              if (displayText && !isInternalMessage(displayText)) {
                queueTeacherText(displayText);
              }
            } else if (msg.speaker === 'student' && msg.text && !isInternalMessage(msg.text)) {
              setTranscript(prev => [...prev, { speaker: 'student', text: msg.text, timestamp: msg.timestamp }]);
              setInteractionCount(prev => prev + 1);
            }
          });
        }
      }
    } catch (error) { addLog(`Poll error: ${error.message}`); }
  }, [sessionId, lastMessageIndex, getApiBaseUrl, addLog, queueTeacherText, processMarkers]);

  useEffect(() => {
    if (isConnected && sessionId) { pollIntervalRef.current = setInterval(pollTranscripts, 1000); }
    else if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, [isConnected, sessionId, pollTranscripts]);

  const setupTrackListeners = useCallback(() => {
    if (!rtviClientRef.current) return;
    rtviClientRef.current.on(RTVIEvent.TrackStarted, (track, p) => { if (!p?.local && track.kind === 'audio') setupAudioTrack(track); });
    rtviClientRef.current.on(RTVIEvent.UserTranscript, (data) => {
      if (data.text && !isInternalMessage(data.text)) {
        setTranscript(prev => [...prev, { speaker: 'student', text: data.text, timestamp: new Date().toISOString() }]);
        setInteractionCount(prev => prev + 1);
      }
    });
    rtviClientRef.current.on(RTVIEvent.BotStartedSpeaking, () => { setIsSpeaking(true); isBotSpeakingRef.current = true; if (botSpeakingCooldownRef.current) { clearTimeout(botSpeakingCooldownRef.current); botSpeakingCooldownRef.current = null; } });
    rtviClientRef.current.on(RTVIEvent.BotStoppedSpeaking, () => {
      botSpeakingCooldownRef.current = setTimeout(() => { isBotSpeakingRef.current = false; setIsSpeaking(false); setCurrentTeacherText(''); }, 1500);
    });
  }, [setupAudioTrack]);

  // ═══════════════════════════════════════
  // CONNECTION
  // ═══════════════════════════════════════
  const startTeaching = async () => {
    try {
      setIsConnecting(true); setConnectionStatus('Connecting...');
      const newSessionId = `teach_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 11)}`;
      setSessionId(newSessionId);
      audioContextRef.current = new AudioContext(); await audioContextRef.current.resume();
      const params = new URLSearchParams({ candidate_id: candidateId, candidate_name: candidateName, candidate_email: candidateEmail, language: 'en-US', duration: duration.toString(), voice: selectedVoice, session_id: newSessionId });
      let baseUrl = getApiBaseUrl();
      if (resumeSummary) params.append('resume_summary', resumeSummary);
      const transport = new WebSocketTransport();
      const client = new RTVIClient({
        transport, params: { baseUrl, endpoints: { connect: `/api/course-teaching/ws/teach/${courseId}/${moduleId}?${params.toString()}` } },
        enableMic: true, enableCam: false,
        callbacks: {
          onConnected: () => { setIsConnected(true); setIsConnecting(false); setConnectionStatus('Connected'); setIsMicEnabled(true); startTimer(); const tracks = rtviClientRef.current?.tracks(); if (tracks?.bot?.audio) setupAudioTrack(tracks.bot.audio); },
          onDisconnected: () => { setIsConnected(false); setConnectionStatus('Session ended'); setIsMicEnabled(false); setIsSpeaking(false); stopTimer(); if (!endSessionDetectedRef.current) { onTeachingEnd({ transcript, whiteboardLines, codeContent, quizStats, completedTopics, engagementScore }); } },
          onBotReady: () => { const tracks = rtviClientRef.current?.tracks(); if (tracks?.bot?.audio) setupAudioTrack(tracks.bot.audio); },
          onError: () => { setConnectionStatus('Error'); setIsConnecting(false); },
        }, timeout: 30000,
      });
      rtviClientRef.current = client; setupTrackListeners(); await client.initDevices(); await client.connect();
    } catch (error) { setConnectionStatus('Failed'); setIsConnecting(false); if (rtviClientRef.current) try { await rtviClientRef.current.disconnect(); } catch(e) {} }
  };

  const endTeaching = async () => { stopTimer(); setShowEndSessionPopup(false); endSessionDetectedRef.current = false; if (rtviClientRef.current) { try { await rtviClientRef.current.disconnect(); } catch(e) {} rtviClientRef.current = null; } setIsConnected(false); setIsMicEnabled(false); onTeachingEnd({ transcript, whiteboardLines, codeContent, quizStats, completedTopics, engagementScore }); };
  const handleExitClick = () => { if (onExit) onExit(); else endTeaching(); };
  const toggleMic = async () => { if (!rtviClientRef.current) return; try { await rtviClientRef.current.enableMic(!isMicEnabled); setIsMicEnabled(!isMicEnabled); } catch(e) { const tracks = rtviClientRef.current.tracks(); if (tracks?.local?.audio) { tracks.local.audio.enabled = !isMicEnabled; setIsMicEnabled(!isMicEnabled); } } };
  const toggleSpeaker = () => { if (audioElementRef.current) { audioElementRef.current.muted = !audioElementRef.current.muted; setIsSpeakerEnabled(!audioElementRef.current.muted); } };
  const clearWhiteboard = () => setWhiteboardLines([]);
  const startTimer = () => { timerIntervalRef.current = setInterval(() => { setTimeRemaining(prev => { if (prev <= 1) { stopTimer(); if (!endSessionDetectedRef.current) { endSessionDetectedRef.current = true; setShowEndSessionPopup(true); } return 0; } return prev - 1; }); }, 1000); };
  const stopTimer = () => { if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; } };
  const formatTime = (s) => `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}`;

  useEffect(() => { return () => { stopTimer(); [textTimeoutRef, textQueueRef, botSpeakingCooldownRef, challengeIntervalRef].forEach(r => { if (r.current) clearTimeout(r.current); }); if (rtviClientRef.current) rtviClientRef.current.disconnect(); }; }, []);

  // Quiz answer handler
  const handleQuizAnswer = (answer) => {
    setQuizAnswer(answer);
    const letter = answer.charAt(0);
    const isCorrect = letter === currentQuiz.correctAnswer;
    setQuizResult(isCorrect ? 'correct' : 'wrong');
    setQuizStats(prev => ({ correct: prev.correct + (isCorrect ? 1 : 0), total: prev.total + 1 }));
    setInteractionCount(prev => prev + 2);
    // Auto-dismiss after 3s
    setTimeout(() => { setCurrentQuiz(null); setQuizAnswer(null); setQuizResult(null); }, 3000);
  };

  const filteredTranscript = transcript
    .map(msg => ({ ...msg, text: msg.speaker === 'student' ? msg.text : cleanDisplayText(msg.text || '') }))
    .filter(msg => msg.speaker === 'student' ? msg.text?.length > 2 : (msg.text?.trim().length > 5 && !isInternalMessage(msg.text)));
  const topicsProgress = moduleTopics.length > 0 ? (completedTopics.length / moduleTopics.length) * 100 : 0;
  const difficultyColors = { beginner: 'text-green-500', intermediate: 'text-amber-500', advanced: 'text-red-500' };
  const difficultyBg = { beginner: 'bg-green-500', intermediate: 'bg-amber-500', advanced: 'bg-red-500' };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <audio ref={audioElementRef} autoPlay playsInline />

      {/* ═══ HEADER ═══ */}
      <header className="bg-[#0a1929] px-4 py-2.5 shadow-lg z-20">
        <div className="flex items-center justify-between max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <BookOpen className="text-cyan-400" size={18} />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-white leading-tight">{moduleTitle}</h1>
              <p className="text-[10px] text-cyan-300/60">{courseTitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Difficulty Badge */}
            {isConnected && (
              <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10`}>
                <TrendingUp size={12} className={difficultyColors[difficultyLevel]} />
                <span className={`text-[10px] font-semibold uppercase ${difficultyColors[difficultyLevel]}`}>{difficultyLevel}</span>
              </div>
            )}

            {/* Engagement Score */}
            {isConnected && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
                <Zap size={12} className="text-yellow-400" />
                <span className="text-[10px] font-semibold text-yellow-400">{engagementScore}%</span>
              </div>
            )}

            {/* Quiz Score */}
            {isConnected && quizStats.total > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
                <Star size={12} className="text-emerald-400" />
                <span className="text-[10px] font-semibold text-emerald-400">{quizStats.correct}/{quizStats.total}</span>
              </div>
            )}

            {/* Timer */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${
              timeRemaining < 60 ? 'bg-red-500/20 text-red-300 border-red-500/30' :
              timeRemaining < 180 ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' :
              'bg-white/10 text-cyan-300 border-white/10'}`}>
              <Clock size={13} />
              <span className="font-mono font-semibold text-xs">{formatTime(timeRemaining)}</span>
            </div>

            {isConnected && (
              <>
                {/* View Switcher */}
                <div className="flex items-center bg-white/10 rounded-full p-0.5">
                  {[...(slidesLoaded ? [{ key: 'slides', icon: BookOpen, label: 'Slides' }] : []), { key: 'whiteboard', icon: Presentation, label: 'Board' }, { key: 'code', icon: Code, label: 'Code' }, { key: 'notes', icon: FileText, label: 'Notes' }].map(({ key, icon: Icon, label }) => (
                    <button key={key} onClick={() => setActiveView(key)} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${activeView === key ? 'bg-cyan-500 text-white shadow' : 'text-white/50 hover:text-white/80'}`}>
                      <Icon size={12} />{label}
                    </button>
                  ))}
                </div>
                {/* Controls */}
                <div className="flex items-center gap-1">
                  <button onClick={toggleMic} className={`p-1.5 rounded-full transition-all ${isMicEnabled ? 'bg-green-500 text-white' : 'bg-white/10 text-white/50'}`}><Mic size={14} /></button>
                  <button onClick={toggleSpeaker} className={`p-1.5 rounded-full transition-all ${isSpeakerEnabled ? 'bg-cyan-500 text-white' : 'bg-white/10 text-white/50'}`}><Volume2 size={14} /></button>
                  <button onClick={handleExitClick} className="px-2.5 py-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-full text-[10px] font-medium transition-colors flex items-center gap-1 ml-1"><X size={12} />End</button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ═══ TOPIC PROGRESS BAR ═══ */}
      {isConnected && moduleTopics.length > 0 && (
        <div className="bg-white border-b border-gray-100 px-4 py-2">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex-shrink-0">Progress</span>
              <div className="flex items-center gap-1 flex-1">
                {moduleTopics.map((topic, idx) => {
                  const isDone = completedTopics.some(ct => ct.toLowerCase().includes(topic.toLowerCase().slice(0, 10)) || topic.toLowerCase().includes(ct.toLowerCase().slice(0, 10)));
                  return (
                    <div key={idx} className="flex items-center gap-1">
                      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${isDone ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                        {isDone ? <CheckCircle2 size={10} className="text-emerald-500" /> : <span className="w-3.5 h-3.5 rounded-full border border-gray-300 flex items-center justify-center text-[8px]">{idx+1}</span>}
                        <span className="max-w-[80px] truncate">{topic}</span>
                      </div>
                      {idx < moduleTopics.length - 1 && <ChevronRight size={10} className="text-gray-300 flex-shrink-0" />}
                    </div>
                  );
                })}
              </div>
              <span className="text-[10px] font-semibold text-gray-400 flex-shrink-0">{Math.round(topicsProgress)}%</span>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MAIN CONTENT ═══ */}
      <main className="flex-1 flex flex-col overflow-hidden relative">
        {!isConnected ? (
          /* ═══ START SCREEN ═══ */
          <div className="h-full flex items-center justify-center p-6">
            <div className="max-w-md w-full">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-[#0a1929] px-8 py-8 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent"></div>
                  <div className="relative">
                    <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-cyan-500/20 mb-3 ${isConnecting ? 'animate-pulse' : ''}`}>
                      {isConnecting ? <div className="animate-spin rounded-full h-7 w-7 border-2 border-cyan-400 border-t-transparent"></div> : <BookOpen className="text-cyan-400" size={24} />}
                    </div>
                    <h2 className="text-lg font-bold text-white mb-1">{moduleTitle}</h2>
                    <p className="text-cyan-300/60 text-xs">{courseTitle}</p>
                  </div>
                </div>
                <div className="p-6">
                  {!isConnecting ? (
                    <>
                      {/* Features Grid */}
                      <div className="grid grid-cols-2 gap-2 mb-5">
                        {[
                          { icon: Clock, text: `${duration} min session`, color: 'text-blue-500' },
                          { icon: HelpCircle, text: 'Live quizzes', color: 'text-purple-500' },
                          { icon: Trophy, text: 'Challenges', color: 'text-amber-500' },
                          { icon: Brain, text: 'Adaptive learning', color: 'text-pink-500' },
                          { icon: Presentation, text: 'Whiteboard + Code', color: 'text-emerald-500' },
                          { icon: Zap, text: 'Engagement tracking', color: 'text-yellow-500' },
                        ].map(({ icon: Icon, text, color }, i) => (
                          <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
                            <Icon size={14} className={color} />
                            <span className="text-xs text-gray-600">{text}</span>
                          </div>
                        ))}
                      </div>
                      {/* Voice Selection */}
                      <div className="mb-4">
                        <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">AI Teacher Voice</label>
                        <select value={selectedVoice} onChange={e => setSelectedVoice(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white text-gray-800 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                          <optgroup label="Female">{voiceOptions.filter(v => v.gender === 'female').map(v => <option key={v.value} value={v.value}>{v.emoji} {v.label}</option>)}</optgroup>
                          <optgroup label="Male">{voiceOptions.filter(v => v.gender === 'male').map(v => <option key={v.value} value={v.value}>{v.emoji} {v.label}</option>)}</optgroup>
                        </select>
                      </div>
                      {resumeSummary && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 mb-4 flex items-center gap-2">
                          <RotateCcw size={14} className="text-amber-600" />
                          <p className="text-xs text-amber-700">Resuming from where you left off</p>
                        </div>
                      )}
                      <button onClick={startTeaching} className="w-full py-3 bg-[#0a1929] hover:bg-[#0d2137] text-white rounded-xl font-semibold text-sm shadow-lg transition-all flex items-center justify-center gap-2">
                        <Play size={16} /> {resumeSummary ? 'Resume Session' : 'Start Learning'}
                      </button>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-600 text-sm font-medium">Connecting to AI Teacher...</p>
                      <p className="text-xs text-gray-400 mt-1">{connectionStatus}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ═══ ACTIVE SESSION ═══ */
          <div className="flex-1 flex overflow-hidden relative">
            {/* Main Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Teacher Speech */}
              <div className={`transition-all duration-500 ${currentTeacherText || isSpeaking ? 'opacity-100 max-h-32' : 'opacity-0 max-h-0 overflow-hidden'}`}>
                <div className="bg-[#0a1929] mx-3 mt-2 rounded-xl shadow-lg">
                  <div className="px-4 py-2.5 flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0 ${isSpeaking ? 'animate-pulse' : ''}`}>
                      <Volume2 size={16} className="text-cyan-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm leading-relaxed">{currentTeacherText || 'Speaking...'}</p>
                    </div>
                    {isSpeaking && (
                      <div className="flex items-end gap-0.5 h-5">
                        {[3, 4, 2, 5, 3].map((h, i) => (
                          <div key={i} className="w-0.5 bg-cyan-400 rounded-full animate-pulse" style={{ height: `${h * 3}px`, animationDelay: `${i * 0.12}s` }}></div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Learning Area */}
              <div className="flex-1 overflow-hidden p-3">
                {/* Whiteboard */}
                {activeView === 'whiteboard' && (
                  <div className="h-full bg-white rounded-xl shadow border border-gray-200 flex flex-col">
                    <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2"><Presentation size={14} className="text-[#0a1929]" /><span className="font-semibold text-gray-700 text-xs">Learning Board</span></div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400">{whiteboardLines.length} items</span>
                        {whiteboardLines.length > 0 && <button onClick={clearWhiteboard} className="text-[10px] text-gray-400 hover:text-red-500 flex items-center gap-0.5"><Eraser size={10} />Clear</button>}
                      </div>
                    </div>
                    <div ref={whiteboardRef} className="flex-1 overflow-auto p-4">
                      {whiteboardLines.length === 0 && diagrams.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-300">
                          <Presentation size={40} className="mb-2" />
                          <p className="text-sm text-gray-400">Waiting for content...</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {whiteboardLines.map((line, i) => {
                            const isHeading = line.startsWith('#') || line.startsWith('Module:') || line.startsWith('Topics') || line.endsWith(':');
                            return (
                              <div key={i} className={`px-3 py-1.5 rounded-lg transition-all duration-300 ${i === whiteboardLines.length - 1 ? 'bg-cyan-50 border-l-3 border-[#0a1929]' : isHeading ? 'bg-gray-50' : ''}`}>
                                <p className={`font-mono whitespace-pre-wrap ${isHeading ? 'text-[#0a1929] font-bold text-sm' : 'text-gray-700 text-xs'}`}>{line}</p>
                              </div>
                            );
                          })}
                          {diagrams.map((diagram, idx) => {
                            const lines = diagram.description.split('\n').filter(l => l.trim());
                            const titleLine = lines.find(l => l.toLowerCase().includes('title:'));
                            const title = titleLine ? titleLine.replace(/title:/gi, '').trim() : `Diagram ${idx + 1}`;
                            return (
                              <div key={`d${idx}`} className="mt-4 rounded-xl border border-gray-200 overflow-hidden">
                                <div className="bg-[#0a1929] px-4 py-2 flex items-center gap-2"><span>📊</span><h3 className="text-white text-xs font-medium">{title}</h3></div>
                                <div className="p-4 bg-gray-50"><DiagramRenderer description={diagram.description} title={title} /></div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* Code */}
                {activeView === 'code' && (
                  <div className="h-full bg-[#1e1e2e] rounded-xl shadow flex flex-col">
                    <div className="bg-[#181825] px-4 py-2 flex items-center gap-2 border-b border-[#313244]">
                      <div className="flex gap-1"><div className="w-2 h-2 rounded-full bg-red-400"></div><div className="w-2 h-2 rounded-full bg-yellow-400"></div><div className="w-2 h-2 rounded-full bg-green-400"></div></div>
                      <span className="text-[10px] text-[#6c7086]">Code Examples</span>
                    </div>
                    <div className="flex-1 overflow-auto">{codeContent ? <pre className="p-4 text-xs font-mono text-[#cdd6f4] leading-relaxed whitespace-pre-wrap">{codeContent}</pre> : <div className="h-full flex flex-col items-center justify-center text-[#6c7086]"><Code size={36} className="mb-2" /><p className="text-xs">Code will appear here</p></div>}</div>
                  </div>
                )}
                {/* Notes */}
                {activeView === 'notes' && (
                  <div className="h-full bg-white rounded-xl shadow border border-gray-200 flex flex-col">
                    <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center gap-2"><FileText size={14} className="text-[#0a1929]" /><span className="font-semibold text-gray-700 text-xs">Your Notes</span></div>
                    <textarea value={notesContent} onChange={e => setNotesContent(e.target.value)} placeholder="Take notes..." className="flex-1 p-4 resize-none bg-transparent text-gray-800 text-sm focus:outline-none placeholder-gray-300" />
                  </div>
                )}
                {/* Slides (from PPT upload) */}
                {activeView === 'slides' && moduleSlides.length > 0 && (
                  <div className="h-full bg-white rounded-xl shadow border border-gray-200 flex flex-col">
                    <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BookOpen size={14} className="text-[#0a1929]" />
                        <span className="font-semibold text-gray-700 text-xs">Slide {currentSlideIndex + 1} of {moduleSlides.length}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setCurrentSlideIndex(p => Math.max(0, p - 1))} disabled={currentSlideIndex === 0} className="px-2 py-0.5 text-[10px] rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-30">Prev</button>
                        <button onClick={() => setCurrentSlideIndex(p => Math.min(moduleSlides.length - 1, p + 1))} disabled={currentSlideIndex >= moduleSlides.length - 1} className="px-2 py-0.5 text-[10px] rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-30">Next</button>
                      </div>
                    </div>
                    <div className="flex-1 overflow-auto p-6">
                      {(() => {
                        const slide = moduleSlides[currentSlideIndex];
                        if (!slide) return null;
                        return (
                          <div className="max-w-2xl mx-auto">
                            {slide.header && <h2 className="text-xl font-bold text-gray-900 mb-2">{slide.header}</h2>}
                            {slide.subheader && <p className="text-sm text-gray-500 mb-4">{slide.subheader}</p>}
                            {slide.content_section_1 && <p className="text-sm text-gray-700 mb-4 leading-relaxed whitespace-pre-line">{slide.content_section_1}</p>}
                            {slide.bullet_points && slide.bullet_points.length > 0 && (
                              <ul className="space-y-2 mb-4">
                                {slide.bullet_points.map((bp, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                    <ChevronRight size={14} className="text-cyan-500 mt-0.5 shrink-0" />
                                    {bp}
                                  </li>
                                ))}
                              </ul>
                            )}
                            {slide.code_snippet && (
                              <div className="mb-4">
                                {slide.code_language && <span className="text-[10px] text-gray-400 uppercase mb-1 block">{slide.code_language}</span>}
                                <pre className="bg-[#1e1e2e] text-[#cdd6f4] p-4 rounded-lg text-xs font-mono overflow-auto">{slide.code_snippet}</pre>
                              </div>
                            )}
                            {slide.content_section_2 && <p className="text-sm text-gray-600 mt-4 whitespace-pre-line">{slide.content_section_2}</p>}
                          </div>
                        );
                      })()}
                    </div>
                    {/* Slide progress bar */}
                    <div className="bg-gray-50 border-t px-4 py-1.5 flex gap-0.5">
                      {moduleSlides.map((_, i) => (
                        <div key={i}
                          className={`h-1 flex-1 rounded-full transition-colors cursor-pointer ${i <= currentSlideIndex ? 'bg-cyan-500' : 'bg-gray-200'}`}
                          onClick={() => setCurrentSlideIndex(i)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ═══ TRANSCRIPT SIDEBAR ═══ */}
            {showTranscript && (
              <div className="w-64 border-l border-gray-200 bg-white flex flex-col">
                <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                  <div className="flex items-center gap-1.5"><MessageSquare size={12} className="text-[#0a1929]" /><span className="font-semibold text-gray-700 text-[10px] uppercase tracking-wider">Chat</span></div>
                  <button onClick={() => setShowTranscript(false)} className="p-0.5 rounded hover:bg-gray-200 text-gray-400"><X size={10} /></button>
                </div>
                <div ref={transcriptRef} className="flex-1 overflow-auto p-2 space-y-1.5">
                  {filteredTranscript.length === 0 ? (
                    <div className="text-center text-gray-300 py-8"><MessageSquare size={24} className="mx-auto mb-1" /><p className="text-[10px]">Messages appear here</p></div>
                  ) : filteredTranscript.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.speaker === 'student' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[88%] rounded-lg px-2.5 py-1.5 ${msg.speaker === 'student' ? 'bg-[#0a1929] text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'}`}>
                        <p className="text-[10px] leading-relaxed">{msg.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!showTranscript && <button onClick={() => setShowTranscript(true)} className="fixed right-2 top-24 p-1.5 bg-[#0a1929] rounded-full shadow-lg text-cyan-400 z-10"><MessageSquare size={14} /></button>}

            {/* ═══ QUIZ OVERLAY ═══ */}
            {currentQuiz && (
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-30 animate-in fade-in">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
                    <div className="flex items-center gap-2"><HelpCircle size={20} className="text-white" /><h3 className="text-white font-bold text-sm">Quick Quiz!</h3></div>
                  </div>
                  <div className="p-5">
                    <p className="text-gray-800 font-medium text-sm mb-4">{currentQuiz.question}</p>
                    <div className="space-y-2">
                      {currentQuiz.options.map((opt, i) => {
                        const letter = opt.charAt(0);
                        const isSelected = quizAnswer === opt;
                        const isCorrectOpt = letter === currentQuiz.correctAnswer;
                        let optStyle = 'border-gray-200 hover:border-purple-300 hover:bg-purple-50';
                        if (quizResult) {
                          if (isCorrectOpt) optStyle = 'border-emerald-400 bg-emerald-50 text-emerald-800';
                          else if (isSelected && !isCorrectOpt) optStyle = 'border-red-400 bg-red-50 text-red-800';
                          else optStyle = 'border-gray-200 opacity-50';
                        } else if (isSelected) optStyle = 'border-purple-500 bg-purple-50';
                        return (
                          <button key={i} onClick={() => !quizResult && handleQuizAnswer(opt)} disabled={!!quizResult}
                            className={`w-full text-left px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${optStyle}`}>
                            {opt}
                            {quizResult && isCorrectOpt && <CheckCircle2 size={16} className="inline ml-2 text-emerald-500" />}
                            {quizResult && isSelected && !isCorrectOpt && <X size={16} className="inline ml-2 text-red-500" />}
                          </button>
                        );
                      })}
                    </div>
                    {quizResult && (
                      <div className={`mt-4 p-3 rounded-xl text-sm font-medium text-center ${quizResult === 'correct' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                        {quizResult === 'correct' ? '🎉 Correct! Great job!' : `❌ The answer is ${currentQuiz.correctAnswer}`}
                      </div>
                    )}
                    {!quizResult && <p className="text-xs text-gray-400 mt-3 text-center">Say your answer out loud or tap to select</p>}
                  </div>
                </div>
              </div>
            )}

            {/* ═══ CHALLENGE OVERLAY ═══ */}
            {currentChallenge && challengeActive && (
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-30">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><Trophy size={20} className="text-white" /><h3 className="text-white font-bold text-sm">Challenge!</h3></div>
                      <div className="flex items-center gap-1.5 bg-white/20 px-3 py-1 rounded-full"><Timer size={14} className="text-white" /><span className="text-white font-mono font-bold text-sm">{challengeTimer}s</span></div>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-gray-800 font-medium text-sm mb-3">{currentChallenge.description}</p>
                    <div className="w-full bg-gray-200 rounded-full h-1.5"><div className="h-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all" style={{ width: `${(challengeTimer / currentChallenge.time) * 100}%` }}></div></div>
                    <p className="text-xs text-gray-400 mt-3 text-center">Answer out loud — the AI teacher is listening!</p>
                    <button onClick={() => { setChallengeActive(false); setCurrentChallenge(null); }} className="w-full mt-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl text-xs font-medium transition-colors">Done / Skip</button>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ EXPLAIN-TO-ME BANNER ═══ */}
            {explainTopic && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 animate-in slide-in-from-top">
                <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-xl shadow-lg px-5 py-3 flex items-center gap-3">
                  <Brain size={20} className="text-white" />
                  <div>
                    <p className="text-white text-xs font-bold">Your Turn! Explain:</p>
                    <p className="text-white/90 text-sm font-medium">{explainTopic}</p>
                  </div>
                  <button onClick={() => setExplainTopic(null)} className="ml-2 p-1 rounded-full bg-white/20 hover:bg-white/30 text-white"><X size={14} /></button>
                </div>
              </div>
            )}

            {/* ═══ FLASHCARD REVIEW ═══ */}
            {showFlashcardReview && flashcards.length > 0 && (
              <div className="absolute bottom-3 right-3 z-20 w-72">
                <div className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2"><Sparkles size={14} className="text-white" /><span className="text-white text-xs font-bold">Flashcard Review</span></div>
                    <span className="text-white/70 text-[10px]">{currentFlashcardIndex + 1}/{flashcards.length}</span>
                  </div>
                  <div className="p-4 cursor-pointer min-h-[80px] flex items-center justify-center" onClick={() => setFlashcardFlipped(!flashcardFlipped)}>
                    {!flashcardFlipped ? (
                      <div className="text-center">
                        <p className="text-gray-800 font-semibold text-sm">{flashcards[currentFlashcardIndex]?.term}</p>
                        <p className="text-[10px] text-gray-400 mt-2">Tap to reveal answer</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <p className="text-indigo-700 text-xs leading-relaxed">{flashcards[currentFlashcardIndex]?.definition}</p>
                      </div>
                    )}
                  </div>
                  <div className="px-4 pb-3 flex items-center gap-2">
                    <button onClick={() => { setCurrentFlashcardIndex(prev => Math.max(0, prev - 1)); setFlashcardFlipped(false); }} disabled={currentFlashcardIndex === 0} className="flex-1 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-[10px] font-medium disabled:opacity-30">Prev</button>
                    <button onClick={() => { setCurrentFlashcardIndex(prev => Math.min(flashcards.length - 1, prev + 1)); setFlashcardFlipped(false); }} disabled={currentFlashcardIndex >= flashcards.length - 1} className="flex-1 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg text-[10px] font-medium disabled:opacity-30">Next</button>
                    <button onClick={() => setShowFlashcardReview(false)} className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-400"><X size={12} /></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ═══ END SESSION POPUP ═══ */}
      {showEndSessionPopup && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            maxWidth: '440px', width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <CheckCircle2 size={28} color="white" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: 'bold', color: '#111827' }}>
                Session Complete!
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                The teaching session has finished. You can end now or continue to ask doubts.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={endTeaching}
                style={{
                  backgroundColor: '#10b981', color: 'white', border: 'none',
                  padding: '14px 20px', borderRadius: '10px', cursor: 'pointer',
                  fontSize: '15px', fontWeight: '600',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
              >
                <CheckCircle2 size={18} /> End Session & Complete Module
              </button>

              <button
                onClick={() => { setShowEndSessionPopup(false); }}
                style={{
                  backgroundColor: '#2563eb', color: 'white', border: 'none',
                  padding: '14px 20px', borderRadius: '10px', cursor: 'pointer',
                  fontSize: '15px', fontWeight: '600',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
              >
                <MessageSquare size={18} /> Continue — Ask Doubts
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default LiveCourseTeaching;
